import React, { useContext, useState } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import PropTypes from 'prop-types';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';

/**
 * Atoms
 */
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import Icon from '../../elements/Icon';
import { strFormat } from '../../../helpers/utils';
/**
 * Controls
 */
import TripDuration from '../TripDuration';
import Rating from '../Rating';

const AdvancedFilter = (props, ref) => {
  const {
    tripDuration: { onChange: onChangeTripDuration = () => {}, value: tripDuration } = {},
    rating: { onChange: onChangeRating = () => {}, value: rating } = {},
    tripDurationSettings,
    ratingSettings,
    disabled,
    filterButtonIconProps,
    availableFilters,
    doneButtonLabelKey = 'cmp-advanced-filter-done-button',
    clearButtonLabelKey = 'cmp-advanced-filter-clear-label',
    titleKey = 'cmp-filter-advanced-title',
    showSeparator = true,
    popover = {}
  } = props;

  const { halfStar = false, minValue = 0, starCount = 5 } = ratingSettings;

  const [ratingValue, setRatingValue] = useState(minValue);
  const [tripDurationValue, setTripDurationValue] = useState(undefined);
  const firstElementRef = React.createRef();
  const flexibleRef = React.useRef();

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const label = translate(titleKey, 'Advanced');
  const equals = (object, object1) => {
    return JSON.stringify(object) === JSON.stringify(object1);
  };

  const onStarClick = (nextValue, prevValue, name, e) => {
    if (halfStar && e) {
      const xPos =
        (e.pageX - e.currentTarget.getBoundingClientRect().left) / e.currentTarget.offsetWidth;

      if (xPos <= 0.5) {
        nextValue -= 0.5;
      }
    }

    if (minValue > 0 && nextValue < minValue) {
      nextValue = minValue;
    }
    setRatingValue(nextValue);
  };

  const update = React.useCallback(() => {
    return availableFilters.forEach((filter) => {
      // eslint-disable-next-line default-case
      switch (filter) {
        case 'TripDuration': {
          setTripDurationValue(Boolean(tripDuration) ? tripDuration : {});
          break;
        }
        case 'Rating': {
          setRatingValue(Boolean(rating) ? rating.minimum : 0);
          break;
        }
      }
    });
  }, [availableFilters, rating, tripDuration]);

  const save = () => {
    availableFilters.forEach((item) => {
      switch (item) {
        case 'TripDuration': {
          if (!equals(tripDurationValue, Boolean(tripDuration) ? tripDuration : {})) {
            onChangeTripDuration(tripDurationValue);
          }
          break;
        }
        case 'Rating': {
          if (
            !equals(
              { minimum: ratingValue, maximum: ratingValue },
              Boolean(rating) ? rating : { minimum: 0, maximum: 0 }
            )
          ) {
            onChangeRating({ minimum: ratingValue, maximum: ratingValue });
          }
          break;
        }
        default:
          break;
      }
    });
  };

  const disabledButton = React.useMemo(() => {
    let disabled = true;
    availableFilters.forEach((item) => {
      switch (item) {
        case 'TripDuration': {
          disabled = !Boolean(tripDuration) && equals(tripDurationValue, {}) && disabled;
          break;
        }
        case 'Rating': {
          if (minValue > 0 && rating) {
            let val = rating?.minimum ? rating.minimum !== rating?.maximum : true;
            disabled = val && ratingValue === minValue && disabled;
          } else {
            disabled = !Boolean(rating) && ratingValue === 0 && disabled;
          }
          break;
        }
        default:
          break;
      }
    });
    return disabled;
  }, [availableFilters, tripDuration, tripDurationValue, minValue, rating, ratingValue]);

  const activeFilter = React.useMemo(() => {
    let active = false;
    availableFilters.forEach((item) => {
      switch (item) {
        case 'TripDuration': {
          active = !!(tripDuration || active);
          break;
        }
        case 'Rating': {
          if (minValue > 0) {
            let val = rating?.minimum ? rating.minimum === rating?.maximum : false;
            active = !!(val || active);
          } else {
            active = !!(rating || active);
          }
          break;
        }
        default:
          break;
      }
    });
    return active;
  }, [availableFilters, tripDuration, minValue, rating]);

  const clear = () => {
    availableFilters.forEach((item) => {
      switch (item) {
        case 'TripDuration': {
          if (Boolean(tripDuration)) {
            onChangeTripDuration(undefined);
          }
          setTripDurationValue({});
          break;
        }
        case 'Rating': {
          if (Boolean(rating)) {
            onChangeRating(minValue ? { minimum: minValue, maximum: starCount } : undefined);
          }
          setRatingValue(minValue ? minValue : 0);
          break;
        }
        default:
          break;
      }
    });
  };

  React.useEffect(() => {
    update();
  }, [rating, tripDuration, update]);

  const elements = {
    TripDuration: (
      <TripDuration
        {...tripDurationSettings}
        value={tripDurationValue}
        onChange={(e) => {
          setTripDurationValue(e);
        }}
      />
    ),
    Rating: (
      <Rating
        containerSettings={ratingSettings.containerSettings}
        starCount={starCount}
        title={translate('cmp-number-stars-label', 'Number of stars')}
        ariaLabel={strFormat(
          translate('cmp-number-nights-aria-label', `Number of stars. {rating} star selected`),
          [ratingValue]
        )}
        value={ratingValue}
        minValue={minValue}
        onStarClick={onStarClick}
        renderStarIcon={(index, value) => {
          const settings = index <= value ? ratingSettings.fillStars : ratingSettings.emptyStars;
          return <Icon {...settings} />;
        }}
        halfStar={halfStar}
        renderStarIconHalf={() => {
          return <Icon {...ratingSettings.halfStars} />;
        }}
      />
    )
  };
  const filtersToDisplay = availableFilters.filter((e) => elements[e]);
  var timer;
  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <FlexibleFilterContainer
          ref={flexibleRef}
          showSeparator
          saveOnClose={false}
          variant="layout.popover"
          label={label}
          title={label}
          disabledClearButton={disabledButton}
          disabledDoneButton={disabledButton}
          enabledDoneButton
          enabledClearButton
          customAccessibilityToggleElement={true}
          labelBottomBtn={translate(doneButtonLabelKey, 'Done')}
          labelClearBtn={translate(clearButtonLabelKey, 'Clear')}
          onClearBtn={() => clear()}
          onDismiss={() => {
            update();
          }}
          onOpenPopup={() => {
            setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
            update();
          }}
          toggleElement={
            <FilterButton
              iconSettings={filterButtonIconProps}
              value={label}
              active={activeFilter}
              disabled={disabled} // used to disabled the component
              onFocus={() => {
                if (flexibleRef.current.isOpen) {
                  timer = setTimeout(() => {
                    flexibleRef.current.close();
                  }, 150);
                }
              }}
              onBlur={() => {
                clearTimeout(timer);
              }}
            />
          }
          onSave={() => save()}
          {...popover}
        >
          <Panels showSeparator={showSeparator}>
            {filtersToDisplay.map((e, index) => {
              if (index === 0) {
                const temporalElement = React.cloneElement(elements[e], { ref: firstElementRef });
                return temporalElement;
              }
              return elements[e];
            })}
          </Panels>
        </FlexibleFilterContainer>
      </ThemeProvider>
    </EmotionCache>
  );
};

AdvancedFilter.propTypes = {
  disabled: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(PropTypes.oneOf(['TripDuration', 'RatingStar'])),
  tripDurationSettings: PropTypes.objectOf(PropTypes.any),
  ratingSettings: PropTypes.objectOf(PropTypes.any),
  tripDuration: PropTypes.shape({
    value: PropTypes.object,
    onChange: PropTypes.func
  }),
  rating: PropTypes.shape({
    value: PropTypes.object,
    onChange: PropTypes.func
  })
};
AdvancedFilter.defaultProps = {
  disabled: false,
  availableFilters: ['TripDuration', 'Rating'],
  tripDurationSettings: {},
  ratingSettings: {},
  tripDuration: {},
  rating: {}
};
export default React.forwardRef(AdvancedFilter);
